import React from 'react';
import userDefinedFunctionHelper from './UserDefinedFunctionHelper';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import Assignment from '../../../types/functions/Assignment/Assignment';
import locationType from '../../../types/functions/Location/LocationType';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import ReportConfig from '../../../types/report/ReportConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import ErrorList from '../../Common/ErrorList/ErrorList';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import { getColumns } from './UserDefinedFunctionBuilder.Grid';
import UserDefinedFunctionSelector from './UserDefinedFunctionSelector/UserDefinedFunctionSelector';
import WarningModal from '../../Common/Modals/WarningModal/WarningModal';
import { reportType } from '../../../infrastructure/constants/reportType';
import ReportComponentStatus from '../Common/ReportComponentStatus/ReportComponentStatus';
import ComponentType from '../../../types/report/ComponentType';
import { onReferenceSelected } from '../../../infrastructure/helpers/report/reference/helper';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import { onGlobalComponentLinkClick, prepareComponentType } from '../Common/ReferenceComponentHelper/ReferenceComponentHelper';
import CustomDropdownOption from '../../Common/Dropdown/CustomDropdownOption';
import SelectSearch, { SelectedOptionValue, SelectSearchOption } from 'react-select-search';
import Mode from '../../../types/report/Mode';
import copyArray from '../../../infrastructure/helpers/common/copyArray';

interface Props {
    isWaiting: boolean;
    isReadOnly: boolean;
    functionNumber: number | null;
    reportConfig: ReportConfig;
    collections: ReportCollections;
    onSelected: (number: number) => void;
    onChange: (functions: UserDefinedFunction[], deletedFunctionNumber?: number) => void;
    clipboard: ReportLogicLocation | null;
    onClipboardChange: (clipboard: ReportLogicLocation) => void;
    isReadOnlyConfig: boolean;
}
interface State {
    showWarningPopupOnSwap: boolean;
    showWarningModal: boolean;
    swapMessage: string;
    mainSwapFunction: UserDefinedFunction;
    swapFunctionWith: UserDefinedFunction | undefined;
    swapError: string;
    swapOptions: SelectSearchOption[];
    showSpinnerOnRemoveForId: number;
}
class UserDefinedFunctionBuilder extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            showWarningModal: false,
            swapError: '',
            showWarningPopupOnSwap: false,
            swapMessage: '',
            swapOptions: [],
            mainSwapFunction: {} as UserDefinedFunction,
            swapFunctionWith: undefined,
            showSpinnerOnRemoveForId: 0
        }

        this.onAddFunctionClick = this.onAddFunctionClick.bind(this);
        this.onRemoveFunctionClick = this.onRemoveFunctionClick.bind(this);
        this.onNameChange = this.onNameChange.bind(this);
        this.onReturnTypeChange = this.onReturnTypeChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onFunctionChange = this.onFunctionChange.bind(this);
        this.getContent = this.getContent.bind(this);
        this.onRemoveFunctionApproved = this.onRemoveFunctionApproved.bind(this);
        this.onFunctionReferenceSelected = this.onFunctionReferenceSelected.bind(this);
        this.getUdfReferences = this.getUdfReferences.bind(this);
        this.onGlobalUdfModeClick = this.onGlobalUdfModeClick.bind(this);
        this.onSwapConfirmed = this.onSwapConfirmed.bind(this);
        this.isSwapOptionReadOnly = this.isSwapOptionReadOnly.bind(this);
        this.onChangeModeClick = this.onChangeModeClick.bind(this);
        this.onGlobalOptionChangeForSwap = this.onGlobalOptionChangeForSwap.bind(this);
    }

    onAddFunctionClick(): void {

        let udf = userDefinedFunctionHelper.getDefault();

        if (this.props.reportConfig.type === reportType.accuracy) {

            let now = new Date(Date.now());
            udf.lastUpdatedTime = now.toISOString().slice(0, -5); // Remove '.fffZ'
        }

        userDefinedFunctionHelper.add(this.props.reportConfig.userDefinedFunctions, udf);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    async onRemoveFunctionClick(): Promise<void> {

        let id = this.props.reportConfig.userDefinedFunctions.find(x => x.number === this.props.functionNumber as number)?.id || 0;
        
        this.setState({ showSpinnerOnRemoveForId: id });

        let references = await userDefinedFunctionHelper.getUdfReferences(this.props.functionNumber as number, this.props.reportConfig, this.props.collections);

        if (references.length) {
            let validationError = 'The function cannot be deleted because is referenced in:\r\n';

            references.forEach((ref) => {

                validationError += `${ref.component.name}\r\n`;
            });

            let location = this.getLocation(this.props.functionNumber as number, []);

            let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

            target.errors = [validationError];

            this.setState({ showSpinnerOnRemoveForId: 0 });

            this.props.onChange(this.props.reportConfig.userDefinedFunctions);

            return;
        }

        this.setState({ showWarningModal: true, showSpinnerOnRemoveForId: 0 });
    }

    onRemoveFunctionApproved() {

        let location = this.getLocation(this.props.functionNumber as number, []);

        let reduced = this.props.reportConfig.userDefinedFunctions.filter(x => x.number !== location.statementNumber);

        this.props.onChange(reduced, this.props.functionNumber as number);

        this.setState({ showWarningModal: false });
    }

    onNameChange(name: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { name: name } as UserDefinedFunction);
    }

    async onReturnTypeChange(e: React.ChangeEvent<HTMLElement>, dataType: string, number: number): Promise<void> {

        let references = await userDefinedFunctionHelper.getUdfReferences(this.props.functionNumber as number, this.props.reportConfig, this.props.collections);

        let location = this.getLocation(number, []);

        if (references.length > 0) {
            e.preventDefault();

            let configs = references.map(x => x.component.name).join(', ');

            let error = `The function is referenced in cofigs: ${configs}. Please remove references first.`

            this.onFunctionChange(location, { errors: [error] } as UserDefinedFunction);

            return;
        }


        this.onFunctionChange(location, { returnType: dataType } as UserDefinedFunction);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { businessDescription: businessDescription } as UserDefinedFunction);
    }

    getLocation(number: number, argumentIndexes: number[]): ReportLogicLocation {

        let location = {} as ReportLogicLocation;

        location.statement = locationType.statement.userDefinedFunction;
        location.statementNumber = number;
        location.argumentIndexes = argumentIndexes;

        return location;
    }

    onFunctionChange(location: ReportLogicLocation, changes: UserDefinedFunction): void {

        let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

        Object.assign(target, changes);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    getContent(func: UserDefinedFunction): JSX.Element {

        return (
            <>
                {
                    this.props.children
                }

                <ErrorList errors={func?.errors || []} className="mt-2" />

                <ReportComponentStatus
                    reportType={this.props.reportConfig.type}
                    lastUpdatedTime={func.lastUpdatedTime}
                    isReadOnly={this.props.isReadOnly} />
            </>
        );
    }

    onFunctionReferenceSelected(option: CustomDropdownOption) {
        if (option.asUrl && this.props.reportConfig.type === reportType.globalModule) {

            let type = prepareComponentType(option.component.componentType);

            onGlobalComponentLinkClick(option.component.componentNumber, type, option.component.reportConfigId, option.component.componentInnerType, option.component.mainComponentNumber);
        }
        else if (option?.component.name?.startsWith(ComponentType.UserDefinedFunction)) {
            this.props.onSelected(option.component.componentNumber || option.component.mainComponentNumber as number);
        }
        else {
            onReferenceSelected(option.component.htmlId || '');
        }
    }

    onGlobalUdfModeClick(globalFunctionId: number) {

        let functionNumber = this.props.collections.globalModuleConfig.userDefinedFunctions.find(x => x.id === globalFunctionId)?.number as number;

        onGlobalComponentLinkClick(functionNumber, ComponentType.UserDefinedFunction, this.props.collections.globalModuleConfig.id);
    }

    onChangeModeClick(func: UserDefinedFunction) {

        let message;

        let swapOptions = [] as SelectSearchOption[];

        if (func.mode === Mode.Global) {
            message = 'The Global function will be converted into a Local function specific to the current configuration.';
        }
        else {
            message = `The existing function, it's parameters and logic will be overwritten.`;
        }

        let configFunctionsIds = this.props.reportConfig.userDefinedFunctions.filter(x => x.mode === Mode.Global).map(x => x.globalUDFModuleId);

        let globalFunctions = this.props.collections.globalModuleConfig.userDefinedFunctions.filter(x => !configFunctionsIds.includes(x.id as number) &&
            x.returnType === func.returnType);

        globalFunctions.forEach((f) => {
            swapOptions.push({
                name: `${f.name}`,
                value: f?.id ? f.id.toString() : ''
            });
        });

        this.setState({ mainSwapFunction: func, swapMessage: message, swapOptions, showWarningPopupOnSwap: true });
    }

    onSwapConfirmed() {

        let selectedFunction;

        if (this.state.swapFunctionWith && Object.keys(this.state.swapFunctionWith).length > 0) {
            selectedFunction = copyObject(this.state.swapFunctionWith);
        }
        else if (this.state.mainSwapFunction.mode === Mode.Local && this.state.swapOptions.length > 0) {
            selectedFunction = this.props.collections.globalModuleConfig?.userDefinedFunctions?.find(x => x.id?.toString() === this.state.swapOptions[0]?.value) as UserDefinedFunction;
        }

        let functions = copyArray(this.props.reportConfig.userDefinedFunctions);

        let func = functions.find(x => x.number === this.state.mainSwapFunction.number) as UserDefinedFunction;

        func.mode = func.mode === Mode.Global ? Mode.Local : Mode.Global;

        func.globalUDFModuleId = func.mode === Mode.Global ? selectedFunction?.id || 0 : undefined;

        if (selectedFunction) {

            func.name = `global ${selectedFunction.name}` || '';
            func.businessDescription = selectedFunction.businessDescription || '';
            func.assignment = copyObject(selectedFunction.assignment) as Assignment;
            func.parameters = selectedFunction.parameters;
            func.parameters.forEach((p) => p.errors = []);
        }

        this.props.onChange(functions);

        this.setState({ swapFunctionWith: undefined, showWarningPopupOnSwap: false });
    }

    isSwapOptionReadOnly(func: UserDefinedFunction): boolean {

        if (func.mode === Mode.Global) {
            return false;
        }

        return this.props.collections.globalModuleConfig?.userDefinedFunctions?.filter(x =>
            !this.props.reportConfig.userDefinedFunctions.filter(x => x.mode === Mode.Global).map(x => x.name).includes(x.name) &&
            x.returnType === func.returnType).length === 0;
    }

    onGlobalOptionChangeForSwap(selectedValue: SelectedOptionValue | SelectedOptionValue[]) {

        let selectedFunction = this.props.collections.globalModuleConfig?.userDefinedFunctions.find(x => x.id?.toString() === selectedValue.toString()) as UserDefinedFunction;

        this.setState({ swapFunctionWith: selectedFunction });
    }

    async getUdfReferences(number: number): Promise<CustomDropdownOption[]> {
        return await userDefinedFunctionHelper.getUdfReferences(number, this.props.reportConfig, this.props.collections);
    }

    render(): JSX.Element {

        return (
            <>
                {this.props.functionNumber &&
                    <>
                        <div>
                            <UserDefinedFunctionSelector
                                isWaiting={this.props.isWaiting}
                                functionNumber={this.props.functionNumber || 0}
                                functions={this.props.reportConfig.userDefinedFunctions}
                                onFunctionSelected={this.props.onSelected} />
                        </div>
                        <div>
                            <ReportLogicList
                                isWaiting={this.props.isWaiting}
                                items={this.props.reportConfig.userDefinedFunctions.filter(x => x.number === this.props.functionNumber)}
                                columns={getColumns(this.onNameChange, this.onReturnTypeChange, this.onBusinessDescriptionChange, this.onRemoveFunctionClick,
                                    this.props.isReadOnly, this.onGlobalUdfModeClick, this.onFunctionReferenceSelected,
                                    this.getUdfReferences, this.props.isReadOnly, this.props.reportConfig.type, this.props.reportConfig.id as number,
                                    this.onChangeModeClick, this.isSwapOptionReadOnly,
                                    this.state.showSpinnerOnRemoveForId
                                )}
                                getContent={this.getContent}
                                getKey={a => a.number}
                                createHtmlId={v => reportConfigNavigation.buildFunctionId(v.number)}
                                children />
                        </div>
                    </>
                }
                {!this.props.isReadOnlyConfig &&
                    <div className="row no-gutters mb-3">
                        <div className="mb-4">
                            <button
                                onClick={this.onAddFunctionClick}
                                className="btn btn-light">
                                + Function
                            </button>
                        </div>
                    </div>
                }
                {this.state.showWarningModal &&
                    <WarningModal
                        onOkClick={this.onRemoveFunctionApproved}
                        onCancelClick={() => this.setState({ showWarningModal: false })}
                        title='Remove item'
                        message='Are you sure you want to delete this item? This action cannot be undone.' />
                }
                {this.state.showWarningPopupOnSwap &&
                    <WarningModal
                        message={this.state.swapMessage}
                        onOkClick={this.onSwapConfirmed}
                        title='Swap Functions'
                        onCancelClick={() => this.setState({ showWarningPopupOnSwap: false })}
                    >{this.state.mainSwapFunction.mode === Mode.Local &&
                        <div className='width-70-percents'>
                            <SelectSearch
                                options={this.state.swapOptions}
                                onChange={this.onGlobalOptionChangeForSwap}
                                search={true}
                                value={this.state.swapOptions?.[0]?.value || ''}
                            />
                        </div>
                        }
                    </WarningModal>
                }
            </>
        );
    }
}

export default UserDefinedFunctionBuilder;
